/**
 * Predicate helpers for detecting terminal failure states on a message part.
 *
 * The actual shape is `MessagePartStatus` from `@assistant-ui/core` — see
 * the structural type below. We accept the structural type so these predicates
 * can be used in plain reducers without dragging the full assistant-ui type
 * graph in.
 */

import { isFailureResult } from '#plugin-chat/lib/tool/result-envelope.ts';

export type PartStatusLike = { type?: string; reason?: string } | undefined;

/** True when a part's status indicates a terminal error. */
export function isPartStatusError(status: PartStatusLike): boolean {
  return status?.type === 'incomplete' && status.reason === 'error';
}

/** True when a part's status indicates the part was cancelled. */
export function isPartStatusCancelled(status: PartStatusLike): boolean {
  return status?.type === 'incomplete' && status.reason === 'cancelled';
}

/**
 * Whether a message part is a tool-call in a terminal failure state. A tool
 * call can signal failure through three distinct channels, all of which fold
 * it into the collapsible run:
 *
 *  - `isError: true` — set by the AI SDK when JSON-parse / input-validation
 *    fails;
 *  - `status.type === 'incomplete'` with reason `error` or `cancelled` —
 *    protocol-level (`output-error`) failures;
 *  - a resolved `{ success: false, … }` result envelope — a thrown `execute`
 *    that the `<Tool>` `runToolCall` boundary caught and converted into a
 *    success-shaped settled part (`status.type === 'complete'`, `isError`
 *    unset). The failure marker lives inside `part.result` (the assistant-ui
 *    `ThreadMessage` tool-call shape these grouping parts come from — NOT the
 *    model-message `output` shape used in `Transcript.tsx`).
 *    Classification is delegated to `isFailureResult`, the same SSOT
 *    `usePartStatus`, `tool-identity` and `bindingProjection` use, so the
 *    channels cannot drift.
 *
 * Pure function; safe to call outside a hook context (e.g. inside the
 * message-parts grouping reducer in `CollapsibleMessageParts`).
 *
 * Non-tool parts (text, reasoning, etc.) always return `false`.
 */
export function isErroredToolPart(part: {
  type: string;
  isError?: boolean;
  status?: PartStatusLike;
  result?: unknown;
}): boolean {
  if (part.type !== 'tool-call') {
    return false;
  }
  if (part.isError === true) {
    return true;
  }
  if (isPartStatusError(part.status) || isPartStatusCancelled(part.status)) {
    return true;
  }
  return isFailureResult(part.result);
}
